'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Trans } from '@lingui/macro';
import { Box, Stack, ToggleButton, Typography } from '@mui/material';
import { Button, TextFieldInput, ToggleButtonGroupInput } from '@prismo-io/design-system';
import { ConfigsStepStatus, type ConfigsStepStatusT, UserLookingForContract } from '@prismo-io/schemas';
import { useRouter } from 'next/navigation';
import { type FC, type ReactNode, startTransition, useMemo } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import type { ResultSchemaT as QuestionSchemaT } from '../../../utils/fetch-qa';
import { ANSWER_IDS_WITH_COMPLEMENT, ANSWER_IDS_WITH_JOB_COMPLEMENT, BorgheseSchema, type BorgheseSchemaT } from './schema';
import { useAnswerToQuestion } from './use-answer-to-question';
export type BorgheseFormProps = {
  defaultValues?: Partial<BorgheseSchemaT>;
  onComplete?: SubmitHandler<BorgheseSchemaT>;
  onSkip?: () => void;
  skipBtn?: ReactNode;
  question: QuestionSchemaT;
  status: ConfigsStepStatusT;
  submitBtns?: ReactNode;
  exclusive?: boolean;
};
export const BorgheseForm: FC<BorgheseFormProps> = props => {
  const {
    defaultValues = {} satisfies Partial<BorgheseSchemaT>,
    onComplete = undefined,
    onSkip = undefined,
    question,
    status,
    submitBtns = undefined,
    skipBtn = undefined,
    exclusive = false
  } = props;
  const router = useRouter();
  const {
    trigger: sendAnswers
  } = useAnswerToQuestion(question.id);
  const formMethods = useForm<BorgheseSchemaT>({
    mode: 'all',
    resolver: zodResolver(BorgheseSchema),
    defaultValues: {
      questionId: question.id,
      ...defaultValues
    }
  });
  const {
    control,
    formState: {
      isValid,
      isSubmitting,
      isSubmitSuccessful
    },
    watch
  } = formMethods;
  const onSubmit: SubmitHandler<BorgheseSchemaT> = data => {
    return sendAnswers({
      answerIds: data.selected,
      complement: data.complement,
      lookingForContract: data.lookingForContract
    }, {
      onSuccess: () => {
        startTransition(() => {
          router.refresh();
          if (onComplete) {
            onComplete(data);
          }
        });
      }
    });
  };
  const currentValues = watch();
  const needComplement = useMemo(() => (currentValues.selected || []).some(id => ANSWER_IDS_WITH_COMPLEMENT.includes(id)), [currentValues, question.id]);
  const needJobComplement = useMemo(() => (currentValues.selected || []).some(id => ANSWER_IDS_WITH_JOB_COMPLEMENT.includes(id)), [currentValues, question.id]);
  return <FormProvider {...formMethods} data-sentry-element="FormProvider" data-sentry-component="BorgheseForm" data-sentry-source-file="form.tsx">
      <Stack spacing={4} component="form" onSubmit={formMethods.handleSubmit(onSubmit)} data-sentry-element="Stack" data-sentry-source-file="form.tsx">
        <Stack direction="row" justifyContent="space-between" data-sentry-element="Stack" data-sentry-source-file="form.tsx">
          <Typography variant="h2" data-sentry-element="Typography" data-sentry-source-file="form.tsx">{question.translate.sentence}</Typography>
          {status === ConfigsStepStatus.Enum.OPTIONAL && <>
              {!!skipBtn ? skipBtn : <Button variant="text" color="gray" onClick={onSkip}>
                  <Trans>Passer</Trans>
                </Button>}
            </>}
        </Stack>
        <ToggleButtonGroupInput control={control} name="selected" orientation="vertical" disabled={isSubmitting || isSubmitSuccessful} color="primary" exclusive={exclusive} transformToArray data-sentry-element="ToggleButtonGroupInput" data-sentry-source-file="form.tsx">
          {question.answers.map(answer => {
          return <ToggleButton key={answer.id} value={answer.id}>
                {answer.translate.sentence}
              </ToggleButton>;
        })}
        </ToggleButtonGroupInput>
        {needJobComplement && <Stack direction={{
        none: 'column',
        sm: 'row'
      }} gap={2} alignItems={{
        none: 'flex-start',
        sm: 'center'
      }}>
            <Typography fontWeight={700}>
              <Trans>Quel genre d'emploi recherchez-vous ?</Trans>
            </Typography>
            <Box component="div" position="relative" width="100%" flex="1 1 0%" sx={{
          '& > .MuiFormControl-root': {
            width: '100%'
          }
        }}>
              <ToggleButtonGroupInput control={control} name="lookingForContract" exclusive fullWidth orientation="horizontal" disabled={isSubmitting || isSubmitSuccessful} color="primary" sx={{
            width: '100%'
          }}>
                <ToggleButton key="job-type-interim" value={UserLookingForContract.Enum.INTERIM}>
                  <Trans>Intérim</Trans>
                </ToggleButton>
                <ToggleButton key="job-type-permanent" value={UserLookingForContract.Enum['CDD / CDI']}>
                  <Trans>CDI / CDD</Trans>
                </ToggleButton>
              </ToggleButtonGroupInput>
            </Box>
          </Stack>}
        {needComplement && <TextFieldInput control={control} label={`Qui vous a invité ?`} name="complement" required />}
        {!!submitBtns ? submitBtns : <Stack alignItems="center">
            <Button variant="contained" type="submit" disabled={!isValid || isSubmitting || isSubmitSuccessful}>
              Valider
            </Button>
          </Stack>}
      </Stack>
    </FormProvider>;
};